<template lang="pug">
  form.b-crm-project-department(@submit.prevent="submit")
    ._title Отдел и ответственный
    ._fields
      BaseSelect(
        v-model="companyDepartment"
        :options="departmentOptions"
        label="Отдел"
        labelOuter
        hideNotSelected
        :error="departmentError"
        @input="onDepartmentChange"
        )
      //- выбор ответственного включается только после выбора отдела:
      //- список кандидатов зависит от него
      BaseUserField(
        v-model="author"
        :options="authorOptions"
        label="Ответственный"
        labelOuter
        :disabled="!companyDepartment"
        :error="authorError"
        emptyLabel="В отделе нет руководителей и менеджеров"
        )
      p._hint(v-if="!companyDepartment") Сначала выберите отдел
    ._buttons
      Btn(grey type="button" @click="back") Назад
      Btn(grey type="button" @click="cancel") Отмена
      Btn(green submit) Создать
</template>

<script>
import crmModel from '@/model/crm';

import companyApi from '@/api/company';
import departmentMemberApi from '@/api/companyDepartmentMember';

import BaseSelect from '@/components/BaseSelect.vue';
import BaseUserField from '@/components/base/BaseUserField.vue';

// из состава отдела ответственным за проект может быть только руководитель
// или менеджер: у остальных ролей нет прав на создание проекта в этом отделе.
// Руководитель компании и заместители в отдел входить не обязаны — см. authorOptions
const RESPONSIBLE_ROLES = ['head', 'manager'];

/**
 * Второй шаг создания проекта из заявки: отдел и ответственный.
 *
 * Отдельный попап, а не универсальная форма: там поля независимы, а здесь
 * второе поле включается и наполняется в зависимости от первого.
 *
 * Чем резолвится (см. CrmLeadDetailApi#addProject):
 *   { companyDepartment, author }        — «Создать»
 *   { back: true, values: {...} }        — «Назад»: вернуться на первый шаг,
 *                                          values несут выбранное здесь, чтобы
 *                                          при возврате вперёд оно не пропало
 *   false                                — «Отмена» или закрытие попапа
 */
export default {
  name: 'CrmProjectDepartmentPopupWrappable',
  components: { BaseSelect, BaseUserField },
  data() {
    return {
      companyId: null,
      company: null,
      departments: [],
      members: [],
      companyDepartment: null,
      author: null,
      departmentError: '',
      authorError: '',
    };
  },
  computed: {
    departmentOptions() {
      return this.departments.map(d => ({ value: d['@id'], label: d.name }));
    },
    /**
     * Кандидаты в ответственные: руководитель компании и его заместители —
     * всегда, они управляют любым отделом, — плюс руководители и менеджеры
     * выбранного отдела. Один человек может быть и заместителем, и
     * руководителем отдела, поэтому список дедуплицируем по IRI.
     */
    authorOptions() {
      if (!this.companyDepartment) return [];

      const managers = [
        this.company?.owner,
        ...(this.company?.deputies ?? []).map(deputy => deputy.user),
      ];
      const departmentUsers = this.members
        .filter(m => m.department === this.companyDepartment && RESPONSIBLE_ROLES.includes(m.role))
        .map(m => m.employee?.user);

      const users = [...managers, ...departmentUsers].filter(user => user?.['@id']);
      return [...new Map(users.map(user => [user['@id'], user])).values()];
    },
  },
  methods: {
    async init(params) {
      this.companyId = params.companyId;
      // возвращаемся сюда после «Назад» — восстанавливаем выбранное
      this.companyDepartment = params.values?.companyDepartment ?? null;
      this.author = params.values?.author ?? null;

      // без отделов и их состава попап показывать нечего: без отдела бэк всё
      // равно не даст создать проект из заявки (422), поэтому при неудаче
      // закрываемся, а не остаёмся с пустым списком
      const data = await crmModel.run(
        () => Promise.all([
          companyApi.fetchItem(this.companyId, {}, true),
          departmentMemberApi.fetchList({ 'department.company': this.companyId }, true),
        ]),
        'Не удалось загрузить отделы компании',
      );
      if (!data) {
        this.cancel();
        return;
      }

      const [company, members] = data;
      this.company = company;
      this.departments = company.departments ?? [];
      this.members = members.items;
    },
    // сменили отдел — прежний ответственный может в новый отдел не входить
    onDepartmentChange() {
      this.departmentError = '';
      if (!this.authorOptions.some(user => user['@id'] === this.author)) {
        this.author = null;
      }
    },
    submit() {
      this.departmentError = this.companyDepartment ? '' : 'Выберите отдел';
      this.authorError = this.author ? '' : 'Выберите ответственного';
      if (this.departmentError || this.authorError) return;

      this.$emit('finish', {
        companyDepartment: this.companyDepartment,
        author: this.author,
      });
    },
    // Назад — на первый шаг. Выбранное здесь не проверяем: шаг не завершён,
    // требовать заполненные поля незачем, но отдаём его наверх, чтобы вернуть
    // при следующем «Далее»
    back() {
      this.$emit('finish', {
        back: true,
        values: { companyDepartment: this.companyDepartment, author: this.author },
      });
    },
    cancel() {
      this.$emit('finish', false);
    },
  },
};
</script>

<style scoped lang="scss">
.crm-project-department {
  display: flex;
  flex-direction: column;
  gap: 30px;
  width: 520px;
  max-width: 100%;
  box-sizing: border-box;

  &__title {
    font-size: 36px;
    font-weight: 700;
    line-height: 24px;
    letter-spacing: 0.02em;
    word-break: break-word;
  }

  &__fields {
    display: grid;
    gap: 15px;
    min-width: 0;
  }

  &__hint {
    font-size: 12px;
    color: var(--color-middle-gray);
  }

  &__buttons {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 20px;
  }

  @media (max-width: 576px) {
    gap: 20px;

    &__title {
      font-size: 24px;
      line-height: 1.2;
    }

    &__buttons > * {
      flex: 1 1 auto;
    }
  }
}
</style>
